import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, typography } from '../../theme';
import { designTokens, componentStyles, createComponentStyle } from '../../theme/design-system';
import { Input, InputProps } from './Input';
import { useLocale } from '../../contexts/LocaleContext';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface PasswordInputProps extends Omit<InputProps, 'secureTextEntry' | 'rightIcon' | 'leftIcon'> {
  showToggle?: boolean;
  toggleIconSize?: number;
  toggleIconColor?: string;
}

export const PasswordInput: React.FC<PasswordInputProps> = ({
  showToggle = true,
  toggleIconSize = 20,
  toggleIconColor,
  size = 'md',
  ...props
}) => {
  const styles = useThemedStyles(getStyles);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [screenWidth] = useState(Dimensions.get('window').width);
  const { isRTL } = useLocale();

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  // Responsive sizing
  const getResponsiveHeight = () => {
    if (screenWidth >= designTokens.breakpoints.desktop) {
      return designTokens.sizes.input[size]; // Use the updated sizes
    }
    return designTokens.sizes.input[size];
  };

  // Get responsive icon size
  const getResponsiveIconSize = () => {
    if (screenWidth >= designTokens.breakpoints.desktop) {
      return toggleIconSize - 2; // Slightly smaller on web
    }
    return toggleIconSize;
  };

  // Get icon color
  const getIconColor = () => {
    if (toggleIconColor) return toggleIconColor;
    return colors.textLight;
  };

  // Create right icon for password toggle
  const rightIcon = showToggle ? (
    <TouchableOpacity
      style={styles.eyeIcon}
      onPress={togglePasswordVisibility}
      disabled={props.editable === false}
    >
      <Ionicons
        name={isPasswordVisible ? 'eye-off' : 'eye'}
        size={getResponsiveIconSize()}
        color={getIconColor()}
      />
    </TouchableOpacity>
  ) : undefined;

  return (
    <Input
      {...props}
      latin
      size={size}
      secureTextEntry={!isPasswordVisible}
      leftIcon={isRTL ? rightIcon : undefined}
      rightIcon={isRTL ? undefined : rightIcon}
      autoComplete="off"
      textContentType="none"
      autoCorrect={false}
      autoCapitalize="none"
      spellCheck={false}
      dataDetectorTypes="none"
      inputStyle={[
        props.inputStyle,
        { height: getResponsiveHeight() }
      ]}
    />
  );
};

const getStyles = () => StyleSheet.create({
  eyeIcon: {
    padding: designTokens.spacing.xs,
  },
  label: {
    fontSize: typography.fontSize.sm,
    fontWeight: '500', // medium
    color: colors.textDark,
    marginBottom: designTokens.spacing.xs,
    fontFamily: typography.fontFamily.medium,
  },
});
